import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * A3 工作階段（`GET /api/sessions`，見 docs/DESIGN-admin.md §3 A3／§6.1）。
 *
 * A3 取代了側欄原本的「健康」頁：`section='health'` 由 `App.tsx` alias 到這個分頁，
 * 所以舊書籤／舊習慣不會斷；這一頁也接手原本健康頁的「重連」「刪除」。
 *
 * 版面：
 * - 上半「房間佔用」：自己人依 `service`＋`room` 聚合 → 我方幾顆／上限幾顆／房內幾人／佔比警示。
 * - 下半「session 清單」：站別、帳號、房、在線／休眠、未讀、歸因（`loginMode`／`transport`）、最後活動。
 * - 桌機（≥768px）兩區上下同時可見；手機（<768px）改 tab 切換（佔用／清單），
 *   列操作收進 `…` 選單、每個目標 ≥44px。
 *
 * 為什麼不是直接掛 `<HealthPanel>`：HealthPanel 一列只有「暱稱／站別／狀態／房／最後活動」，
 * A3 還要「帳號、未讀、loginMode、transport」四欄，手機還要把操作收進 `…` 選單（44px）。
 * 直接塞進去會缺欄位、手機按鈕仍是 22px 直排。所以這裡**重用它的純函式**
 * （`healthStatusOf`／`formatLastActivity`）與狀態色票（`cui-health-pill is-*`），
 * 行為語意與 HealthPanel 完全相同：重連＝重打 join；刪除兩段式（第一次只換字、4 秒後自動收回、
 * 不跳 confirm 對話框）。
 *
 * 資料來源：**只吃 props**。`AdminShell` 把宿主的 `ApiClient` 傳進來（`<SessionsPage client={client} />`），
 * 這一頁不再自己讀 `/config.json`、也不再自己拼 base／mock 判斷——
 * 解析只在 `apps/console/src/api.ts` 一處，兩邊各解一次就會出現「同一站兩個設定」的鬼故事。
 * 需要 `loginMode`／`transport`／`account`／`loginReused` 這些 §4.3 歸因欄位，所以讀的是
 * `client.listSessionRows()`（原始列），不是會被 `toConversation()` 濾掉的投影版。
 * 沒拿到 client 時停在「尚未接線」的空狀態，不偷偷 fallback 成 mock（那會讓人以為後端
 * 真的回了一堆假 session）。憑證由宿主 client 帶（`api/session-headers`），問題單 #13。
 * 只讀公開欄位，沒有任何憑證（§5.2）。
 *
 * 三態：載入中／空／錯誤都有各自文字；數字沒有資料時顯示 `—`，不跟 `0` 混用。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EntityTable } from '../EntityTable';
import { formatLastActivity, healthStatusOf, } from '../HealthPanel';
import { toConversation } from '../../types';
/**
 * 同一間房最多幾個自己的帳號：與 `packages/worker/src/limits.ts` 的
 * `DEFAULT_ROOM_ACCOUNT_QUOTA` 同步。被 env 覆寫時前端拿不到，所以上限只是顯示基準（?）。
 */
const ROOM_ACCOUNT_QUOTA = 5;
/** 佔比 ≥ 這個值就標黃（§3 A3「佔比警示」）。 */
const ROOM_SHARE_WARN = 0.5;
/** 寬鬆讀字串：非字串／空字串一律 null（避免把 `undefined` 畫到畫面上）。 */
function str(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}
/** 把一列原始 JSON 收斂成 `SessionRow`：不認識的欄位丟掉，壞一列不讓整份空白。 */
function toSessionRow(raw) {
    const base = toConversation(raw);
    const loginModeRaw = str(raw.loginMode);
    return {
        ...base,
        loginMode: loginModeRaw === 'account' ? 'account' : loginModeRaw === 'guest' ? 'guest' : null,
        account: str(raw.account),
        transport: str(raw.transport),
        loginReused: raw.loginReused === true,
    };
}
/**
 * 讀 `GET /api/sessions`：走宿主傳進來的 `client.listSessionRows()`（原始列，要歸因欄位）。
 * 這一頁不決定 base、不決定 mock——那兩件事只有 `apps/console/src/api.ts` 說了算。
 * 每列都經過 `toSessionRow()` 收斂，欄位缺就是 `—`。
 */
async function fetchSessions(client) {
    if (client.listSessionRows) {
        const rows = await client.listSessionRows();
        return rows
            .filter((item) => typeof item === 'object' && item !== null)
            .map(toSessionRow);
    }
    // 相容：第三方實作只給了投影版／`listAccounts` 時照樣能顯示，只是歸因欄位是 `—`。
    const list = client.listConversations
        ? await client.listConversations()
        : (await client.listAccounts()).map((account) => ({
            ...account,
            unread: 0,
            lastActivityAt: null,
            lastMessagePreview: null,
            roomOnline: null,
            lastReadTs: 0,
        }));
    return list.map((row) => toSessionRow(row));
}
/** 重連＝重打 join（`POST /api/sessions/:id/join`，喚醒 DO 並重新進房）。 */
async function reconnectSession(client, row) {
    await client.joinRoom(row.id, row.room ?? '');
}
/** 刪除＝leave + close（`DELETE /api/sessions/:id`）。 */
async function deleteSession(client, row) {
    await client.close(row.id);
}
/** 對一組 session 做「我方帳號數／房內人數」聚合（沒進房的不列）。 */
function groupByRoom(rows) {
    const map = new Map();
    for (const row of rows) {
        if (!row.room)
            continue;
        const key = `${row.service}:${row.room}`;
        const current = map.get(key);
        if (current) {
            current.mine += 1;
            // 同一間房的 roomOnline 來自同一次站方快取，取最大值避免其中一列沒帶。
            if (row.roomOnline !== null) {
                current.roomOnline =
                    current.roomOnline === null ? row.roomOnline : Math.max(current.roomOnline, row.roomOnline);
            }
            continue;
        }
        map.set(key, { service: row.service, room: row.room, mine: 1, roomOnline: row.roomOnline });
    }
    // 佔比高的排前面（要動手的先看到），同佔比再依房號。
    return [...map.values()].sort((a, b) => {
        const shareA = a.roomOnline && a.roomOnline > 0 ? a.mine / a.roomOnline : 0;
        const shareB = b.roomOnline && b.roomOnline > 0 ? b.mine / b.roomOnline : 0;
        if (shareA !== shareB)
            return shareB - shareA;
        return a.room.localeCompare(b.room);
    });
}
function errMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
/** 手機判斷（<768px）：本 package 沒有 `useIsNarrow`（那是 apps/console 的），這裡自己接 matchMedia。 */
function useNarrow(maxWidth = 767) {
    const query = `(max-width: ${maxWidth}px)`;
    const read = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false;
    const [narrow, setNarrow] = useState(read);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const mql = window.matchMedia(query);
        const onChange = () => setNarrow(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query]);
    return narrow;
}
/* ==========================================================================
 * 畫面
 * ========================================================================== */
/*
 * 桌機清單欄寬已改成 `sessionColumns` 的 `width`（交給 `EntityTable`／`table-fixed`）。
 *
 * 沒有「帳號」欄：`GET /api/sessions` 目前不回 `account`（`toConversation` 也沒有），
 * 留一欄全是 `—` 只是浪費寬度。帳號名若真的有帶，會出現在「歸因」欄的 `帳號 xxx`。
 */
const ROOM_COLUMNS = 'minmax(110px, 1.4fr) 88px 72px 66px minmax(84px, 1fr)';
const headCellStyle = {
    fontFamily: 'var(--ak-font-mono)',
    fontSize: 10,
    letterSpacing: 'var(--ak-tracking-micro)',
    textTransform: 'uppercase',
    color: 'var(--ak-color-text-tertiary)',
};
const touchTarget = { minHeight: 44, minWidth: 44 };
/** 狀態 → 左側色條（沿用 HealthPanel 的 tone）。 */
const TONE_BORDER = {
    online: 'var(--ak-color-success)',
    idle: 'var(--ak-color-primary)',
    limited: 'var(--ak-color-warning)',
    down: 'var(--ak-color-danger)',
};
function loginModeLabel(row) {
    if (row.loginMode === 'account')
        return row.account ? `帳號 ${row.account}` : '帳號';
    if (row.loginMode === 'guest')
        return '訪客';
    return '—';
}
function transportLabel(value) {
    return value ?? '—';
}
/**
 * 手機佔用卡的一格：小標在上、值在下。
 * 手機沒有表頭，裸值（`1/5`、`28`、`4%`、`正常`）看不出各自是什麼意思。
 */
function roomStat(label, value, options = {}) {
    return (_jsxs("span", { style: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }, children: [_jsx("span", { style: { ...headCellStyle, fontSize: 9, lineHeight: 1.2 }, children: label }), _jsx("span", { className: `cui-mono${options.muted ? ' cui-muted' : ''}`, "data-testid": options.testId, style: { fontSize: 12, lineHeight: 1.3, color: options.color }, children: value })] }));
}
export function SessionsPage({ client } = {}) {
    const narrow = useNarrow();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadedAt, setLoadedAt] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const [busyId, setBusyId] = useState(null);
    /** 手機一次只顯示一區（§6.1：兩區改 tab 切換）。 */
    const [region, setRegion] = useState('rooms');
    /** 兩段式刪除：哪一列在等第二次點擊（4 秒後自動收回，與 HealthPanel 一致）。 */
    const [confirmId, setConfirmId] = useState(null);
    /** 手機 `…` 選單一次只開一個。 */
    const [menuId, setMenuId] = useState(null);
    const reqRef = useRef(0);
    const load = useCallback(async (options = {}) => {
        const token = (reqRef.current += 1);
        /** 沒接 client：不算載入中、不算錯誤，停在「尚未接線」（外殼忘了傳就是看得出來）。 */
        if (!client) {
            setRows([]);
            setError(null);
            setLoading(false);
            setLoadedAt(null);
            return;
        }
        if (!options.silent)
            setLoading(true);
        try {
            const list = await fetchSessions(client);
            if (token !== reqRef.current)
                return;
            setRows(list);
            setError(null);
            setLoadedAt(Date.now());
            setNow(Date.now());
        }
        catch (err) {
            if (token !== reqRef.current)
                return;
            setRows([]);
            setError(errMessage(err));
        }
        finally {
            if (token === reqRef.current)
                setLoading(false);
        }
    }, 
    /** client 換掉（例如重貼 token 後宿主重建）就重新抓一次。 */
    [client]);
    useEffect(() => {
        void load();
    }, [load]);
    /** 即時頁：30 秒自己更新一次（切走就停），不用人手動按。 */
    useEffect(() => {
        const timer = setInterval(() => void load({ silent: true }), 30_000);
        return () => clearInterval(timer);
    }, [load]);
    useEffect(() => {
        if (!confirmId)
            return;
        const timer = setTimeout(() => setConfirmId(null), 4000);
        return () => clearTimeout(timer);
    }, [confirmId]);
    const rooms = useMemo(() => groupByRoom(rows), [rows]);
    /**
     * 狀態只算一次：標題計數與每一列的 pill 都讀同一份 map。
     *
     * 這樣「N/M 在線」與列上的「在線／休眠／斷線／限流」不可能互相打臉——
     * 以前兩邊各自呼叫 `healthStatusOf()`，`now` 與 rows 快照不同步時就會出現
     * 「標題 0/4 在線」但四列都是「在線」的矛盾。
     */
    const statuses = useMemo(() => {
        const map = new Map();
        for (const row of rows)
            map.set(row.id, healthStatusOf(row, now));
        return map;
    }, [rows, now]);
    const onlineCount = useMemo(() => [...statuses.values()].filter((status) => status.tone === 'online').length, [statuses]);
    const unreadTotal = useMemo(() => rows.reduce((sum, row) => sum + row.unread, 0), [rows]);
    const runReconnect = useCallback(async (row) => {
        if (!client)
            return;
        if (!row.room) {
            setError('這顆還沒有房間，重連無處可去；先到房間列表進房');
            return;
        }
        setBusyId(row.id);
        setError(null);
        try {
            await reconnectSession(client, row);
            await load({ silent: true });
        }
        catch (err) {
            setError(errMessage(err));
        }
        finally {
            setBusyId(null);
        }
    }, [client, load]);
    const runDelete = useCallback(async (row) => {
        if (!client)
            return;
        setBusyId(row.id);
        setError(null);
        try {
            await deleteSession(client, row);
            setRows((prev) => prev.filter((item) => item.id !== row.id));
            setLoadedAt(Date.now());
            await load({ silent: true });
        }
        catch (err) {
            setError(errMessage(err));
        }
        finally {
            setBusyId(null);
            setConfirmId(null);
            setMenuId(null);
        }
    }, [client, load]);
    const busy = busyId !== null;
    const header = (_jsxs("header", { className: "cui-panel-head", children: [_jsx("h2", { className: "cui-panel-title", children: "\u5DE5\u4F5C\u968E\u6BB5" }), _jsx("span", { className: "cui-count", "data-testid": "admin-sessions-count", children: !client
                    ? '尚未接線'
                    : loading && rows.length === 0
                        ? '載入中…'
                        : error && rows.length === 0
                            ? '—'
                            : `${onlineCount}/${rows.length} 在線 · 未讀 ${unreadTotal}` }), _jsx("button", { type: "button", className: "cui-icon-btn", onClick: () => void load(), disabled: !client, title: loadedAt ? `最後更新 ${formatLastActivity(loadedAt, now)}` : '重新整理', "aria-label": "\u91CD\u65B0\u6574\u7406\u5DE5\u4F5C\u968E\u6BB5", "data-testid": "admin-sessions-refresh", style: narrow ? touchTarget : undefined, children: "\u21BB" })] }));
    /** 外殼沒傳 client：說清楚「這頁只吃 props」，不假裝在載入、也不偷偷退回 mock。 */
    const unwiredLine = client ? null : (_jsx("p", { className: "cui-empty", "data-testid": "admin-sessions-unwired", children: "\u5C1A\u672A\u63A5\u7DDA\uFF1A\u5DE5\u4F5C\u968E\u6BB5\u9801\u53EA\u5403\u4E0A\u5C64\u50B3\u9032\u4F86\u7684\u8CC7\u6599\u4F86\u6E90\uFF0C\u76EE\u524D\u6C92\u6709\u6536\u5230 client\u3002" }));
    const errorLine = error ? (_jsx("p", { className: "cui-room-error-text", "data-testid": "admin-sessions-error", role: "alert", children: error })) : null;
    /** 手機分區切換（桌機不渲染，兩區直接上下排）。 */
    const regionTabs = narrow ? (_jsx("nav", { className: "cui-tabs", role: "tablist", "aria-label": "\u5DE5\u4F5C\u968E\u6BB5\u5206\u5340", children: [
            { id: 'rooms', label: `佔用 ${rooms.length}` },
            { id: 'sessions', label: `清單 ${rows.length}` },
        ].map((tab) => (_jsx("button", { type: "button", role: "tab", "aria-selected": region === tab.id, className: `cui-tab${region === tab.id ? ' is-active' : ''}`, onClick: () => setRegion(tab.id), "data-testid": "admin-sessions-region-tab", "data-region": tab.id, style: { minHeight: 44 }, children: tab.label }, tab.id))) })) : null;
    /* ── 區塊 1：房間佔用 ─────────────────────────────────────────────────── */
    const roomTable = (_jsxs("div", { className: "flex flex-col", "data-testid": "admin-sessions-rooms", children: [_jsx("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: narrow ? '1fr' : ROOM_COLUMNS,
                    gap: 6,
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--ak-color-border)',
                    ...headCellStyle,
                }, children: narrow ? (_jsxs("span", { children: ["\u623F\u9593\u4F54\u7528 \u00B7 ", rooms.length, " \u9593"] })) : (_jsxs(_Fragment, { children: [_jsx("span", { children: "\u7AD9\u5225\uFF0F\u623F\u9593" }), _jsx("span", { children: "\u6211\u65B9\uFF0F\u4E0A\u9650" }), _jsx("span", { children: "\u623F\u5167\u4EBA\u6578" }), _jsx("span", { style: { textAlign: 'right' }, children: "\u4F54\u6BD4" }), _jsx("span", { children: "\u8B66\u793A" })] })) }), rooms.length === 0 ? (
            /* #736 三態：載入中／讀不到（可重試）／真的沒有，三者必須看得出差別。 */
            _jsxs("div", { className: error ? 'cui-room-error' : 'cui-empty', "data-testid": error ? 'admin-sessions-rooms-error' : loading ? 'admin-sessions-rooms-loading' : 'admin-sessions-rooms-empty', "data-state": !client ? 'unavailable' : error ? 'error' : loading ? 'loading' : 'empty', role: error ? 'alert' : 'status', "aria-busy": loading || undefined, "aria-live": error ? 'assertive' : 'polite', children: [_jsx("p", { className: error ? 'cui-room-error-text' : undefined, children: !client
                            ? '尚未接線'
                            : error
                                ? `房間佔用讀不到（不是「沒人進房」）：${error}`
                                : loading
                                    ? '載入房間佔用中…'
                                    : '沒有自己人進房；先到帳號池建一顆，或到房間列表進房' }), error ? (_jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void load(), "data-testid": "admin-sessions-rooms-retry", children: "\u91CD\u8A66" })) : null] })) : (_jsx("ul", { className: "cui-list", style: { gap: 2 }, children: rooms.map((room) => {
                    const share = room.roomOnline && room.roomOnline > 0 ? room.mine / room.roomOnline : null;
                    const warn = share !== null && share >= ROOM_SHARE_WARN;
                    const full = room.mine >= ROOM_ACCOUNT_QUOTA;
                    return (_jsxs("li", { "data-testid": "admin-rooms-row", "data-service": room.service, "data-room": room.room, "data-warn": warn || full ? 'true' : 'false', style: {
                            display: 'grid',
                            gridTemplateColumns: narrow ? '1fr' : ROOM_COLUMNS,
                            gap: narrow ? 2 : 6,
                            alignItems: 'center',
                            padding: '6px 8px',
                            borderLeft: `3px solid ${warn || full ? 'var(--ak-color-warning)' : 'var(--ak-color-border)'}`,
                            background: 'color-mix(in srgb, var(--ak-color-text) 2%, transparent)',
                            fontSize: 11.5,
                        }, children: [_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: `cui-badge cui-badge--${room.service}`, children: room.service }), _jsx("span", { className: "cui-mono", children: room.room })] }), narrow ? (
                            /**
                             * 手機（<768px）沒有表頭，四個值裸著排會看不出是什麼（1/5、28、4%、正常）。
                             * 每一格自己帶小標：我方／上限、房內、佔比、警示。
                             */
                            _jsxs("span", { "data-testid": "admin-rooms-stats", style: {
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                                    gap: '4px 10px',
                                    width: '100%',
                                    marginTop: 2,
                                }, children: [roomStat('我方／上限', `${room.mine}／${ROOM_ACCOUNT_QUOTA}${full ? ' 滿' : ''}`, {
                                        testId: 'admin-rooms-mine',
                                        color: full ? 'var(--ak-color-warning)' : undefined,
                                    }), roomStat('房內', room.roomOnline === null ? '—' : String(room.roomOnline), {
                                        testId: 'admin-rooms-online',
                                        muted: true,
                                    }), roomStat('佔比', share === null ? '—' : `${Math.round(share * 100)}%`, {
                                        testId: 'admin-rooms-share',
                                        color: warn ? 'var(--ak-color-warning)' : undefined,
                                    }), roomStat('警示', full ? '已滿額' : warn ? '佔比 ≥50%' : '正常', {
                                        color: warn || full ? 'var(--ak-color-warning)' : 'var(--ak-color-text-tertiary)',
                                    })] })) : (_jsxs(_Fragment, { children: [_jsxs("span", { className: "cui-mono", "data-testid": "admin-rooms-mine", children: [room.mine, "\uFF0F", ROOM_ACCOUNT_QUOTA, full ? _jsx("span", { className: "cui-muted", children: " \u6EFF" }) : null] }), _jsx("span", { className: "cui-mono cui-muted", "data-testid": "admin-rooms-online", children: room.roomOnline === null ? '—' : room.roomOnline }), _jsx("span", { className: "cui-mono", "data-testid": "admin-rooms-share", style: { textAlign: 'right', color: warn ? 'var(--ak-color-warning)' : undefined }, children: share === null ? '—' : `${Math.round(share * 100)}%` }), _jsx("span", { style: {
                                            color: warn || full
                                                ? 'var(--ak-color-warning)'
                                                : 'var(--ak-color-text-tertiary)',
                                        }, children: full ? '已滿額' : warn ? '佔比 ≥50%' : '正常' })] }))] }, `${room.service}:${room.room}`));
                }) }))] }));
    /* ── 區塊 2：session 清單 ──────────────────────────────────────────────── */
    /** 桌機：兩個小按鈕並排；語意同 HealthPanel（刪除兩段式）。 */
    const actionsFor = (row) => {
        const confirming = confirmId === row.id;
        const rowBusy = busy && busyId === row.id;
        return (_jsxs("span", { className: "flex items-center gap-1", "data-testid": "admin-session-actions", children: [_jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void runReconnect(row), disabled: busy, title: "\u91CD\u6253 join \u559A\u9192\u9019\u500B\u8EAB\u5206", "data-testid": "admin-session-reconnect", children: rowBusy ? '…' : '重連' }), _jsx("button", { type: "button", className: `cui-btn cui-btn--sm${confirming ? ' cui-btn--danger' : ' cui-btn--ghost'}`, onClick: () => {
                        if (!confirming) {
                            setConfirmId(row.id);
                            return;
                        }
                        setConfirmId(null);
                        void runDelete(row);
                    }, disabled: busy, "data-testid": "admin-session-delete", "data-confirming": confirming ? 'true' : 'false', children: confirming ? '確定刪除' : '刪除' })] }));
    };
    /** status 與標題計數同源（`statuses`），不各自再算一次。 */
    const statusOf = (row) => statuses.get(row.id) ?? healthStatusOf(row, now);
    /**
     * 一份欄位描述：桌機表頭列與手機卡片都從這裡長出來（T11 的 `EntityTable`）。
     * 原本這一區自己寫了 two-width grid＋手機 `…` 選單，欄位集合靠人工對齊；改成同一份之後就沒有漂移空間。
     */
    const sessionColumns = [
        {
            id: 'identity',
            label: '身分',
            width: '22%',
            sortValue: (row) => row.nickname || '（未命名）',
            render: (row) => (_jsx("span", { className: "cui-mono", style: {
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }, title: row.nickname || undefined, children: row.nickname || '（未命名）' })),
        },
        {
            id: 'service',
            label: '站別',
            width: '10%',
            sortValue: (row) => row.service,
            render: (row) => _jsx("span", { className: `cui-badge cui-badge--${row.service}`, children: row.service }),
        },
        {
            id: 'room',
            label: '房間',
            width: '12%',
            render: (row) => (_jsxs("span", { className: "cui-mono", style: { fontSize: 11 }, children: [row.room ?? '—', row.roomNo ? _jsxs("span", { className: "cui-muted", children: [" #", row.roomNo] }) : null] })),
        },
        {
            id: 'status',
            label: '狀態',
            width: '10%',
            sortValue: (row) => statusOf(row).label,
            render: (row) => {
                const status = statusOf(row);
                return (_jsx("span", { className: `cui-health-pill is-${status.tone}`, "data-testid": "admin-session-status", children: status.label }));
            },
        },
        {
            id: 'unread',
            label: '未讀',
            width: '7%',
            align: 'end',
            sortValue: (row) => row.unread,
            render: (row) => (_jsx("span", { className: "cui-mono", "data-testid": "admin-session-unread", style: {
                    display: 'block',
                    fontSize: 11,
                    textAlign: 'end',
                    color: row.unread > 0 ? 'var(--ak-color-text)' : 'var(--ak-color-text-tertiary)',
                }, children: row.unread })),
        },
        {
            id: 'attribution',
            label: '歸因',
            width: '16%',
            render: (row) => (_jsxs("span", { style: { display: 'flex', flexDirection: 'column', gap: 1, fontSize: 10.5 }, children: [_jsx("span", { "data-testid": "admin-session-login-mode", children: loginModeLabel(row) }), _jsxs("span", { className: "cui-mono cui-muted", "data-testid": "admin-session-transport", children: [transportLabel(row.transport), row.loginReused ? ' · 重用' : ''] })] })),
        },
        {
            id: 'activity',
            label: '最後活動',
            width: '13%',
            sortValue: (row) => row.lastActivityAt,
            render: (row) => (_jsx("span", { className: "cui-mono cui-muted", "data-testid": "admin-session-last-activity", title: statusOf(row).reason, style: { fontSize: 11 }, children: formatLastActivity(row.lastActivityAt, now) })),
        },
        {
            id: 'actions',
            label: '操作',
            width: '10%',
            align: 'end',
            render: (row) => actionsFor(row),
        },
    ];
    const sessionList = (_jsx("div", { "data-testid": "admin-sessions-list", children: error && !loading ? (
        // #02：錯誤時不能說「還沒有 session」；那是把「讀不到」講成「真的沒有」。
        _jsxs("div", { className: "cui-room-error", role: "alert", "data-testid": "admin-sessions-list-error", children: [_jsx("p", { className: "cui-room-error-text", children: error }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void load(), "data-testid": "admin-sessions-list-retry", children: "\u91CD\u8A66" })] })) : (_jsx(EntityTable, { rows: rows, columns: sessionColumns, rowKey: (row) => row.id, testId: "admin-sessions-table", rowTestId: "admin-session-row", loading: loading, empty: { title: '還沒有 session', hint: '先到帳號池建一顆' }, rowAttrs: (row) => ({
                'data-session-id': row.id,
                'data-status': statusOf(row).tone,
                'data-login-mode': row.loginMode ?? 'unknown',
                'data-transport': row.transport ?? 'unknown',
            }) })) }));
    return (_jsxs("section", { className: "cui-panel", "data-testid": "admin-page-sessions", "aria-label": "\u5DE5\u4F5C\u968E\u6BB5", children: [header, unwiredLine, errorLine, _jsxs("div", { className: "flex min-h-0 flex-col gap-2 p-2", children: [regionTabs, narrow ? (region === 'rooms' ? (roomTable) : (sessionList)) : (_jsxs(_Fragment, { children: [roomTable, _jsx("div", { style: { borderTop: '1px solid var(--ak-color-border)', margin: '2px 0' } }), sessionList] }))] })] }));
}
//# sourceMappingURL=SessionsPage.js.map